import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  CalendarTodayOutlined,
  FilterListOutlined,
  FileDownloadOutlined,
  ArrowBackIosNew,
  ArrowForwardIos,
  ExpandMore,
  CallReceivedOutlined,
  PhoneInTalkOutlined,
  VerifiedOutlined,
} from "@mui/icons-material";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import CallDetailModal from "../../components/CallDetailModal";
import { StandardTableContainer, useStandardTableStyles } from "../../components/StandardTable";
import {
  outcomeInfo,
  sentimentInfo,
  formatDuration,
  formatFecha,
  transferResultInfo,
  SATISFACTION_LEVELS,
} from "../../helpers/callFormatting";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const PAGE_SIZES = [25, 50, 100];

const useStyles = makeStyles()((theme) => ({
  tableWrap: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  mono: {
    fontFamily: "ui-monospace, Consolas, monospace",
    fontVariantNumeric: "tabular-nums",
  },
  direction: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    color: theme.palette.text.secondary,
  },
  connection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  connectionIcon: {
    fontSize: 16,
    color: theme.palette.text.disabled,
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.25, 1.5),
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
  },
  pager: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  popoverBody: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    minWidth: 260,
  },
  popoverActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  loadingRow: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
}));

const buildOutcomes = () => [
  { value: "", label: i18n.t("callHistory.allOutcomes") },
  { value: "hangup", label: i18n.t("callCommon.outcomes.hangup") },
  { value: "transferred", label: i18n.t("callCommon.outcomes.transferred") },
  { value: "socket_error", label: i18n.t("callCommon.outcomes.socketError") },
  { value: "no_prompt", label: i18n.t("callCommon.outcomes.noPrompt") },
  { value: "server_error", label: i18n.t("callCommon.outcomes.serverError") },
];

const buildResolvedOptions = () => [
  { value: "", label: i18n.t("callHistory.filterAll") },
  { value: "yes", label: i18n.t("callHistory.yes") },
  { value: "no", label: i18n.t("callHistory.no") },
];

const buildSentimentOptions = () => [
  { value: "", label: i18n.t("callHistory.filterAll") },
  ...SATISFACTION_LEVELS.map((level) => ({ value: level, label: sentimentInfo(level)?.label })),
];

const buildAuditOptions = () => [
  { value: "", label: i18n.t("callHistory.filterAll") },
  { value: "audited", label: i18n.t("callHistory.auditAudited") },
  { value: "pending", label: i18n.t("callHistory.auditPending") },
];

const OUTCOME_CHIP_COLOR = {
  hangup: "default",
  colgada: "default",
  transferred: "success",
  socket_error: "error",
  server_error: "error",
  no_prompt: "warning",
};

const EMPTY_FILTERS = {
  outcome: "",
  from: "",
  to: "",
  resolved: "",
  sentiment: "",
  audit: "",
  dateFrom: "",
  dateTo: "",
};

const POPOVER_FILTER_KEYS = ["outcome", "from", "to", "resolved", "sentiment", "audit"];

const CallHistory = () => {
  const { classes } = useStyles();
  const tableClasses = useStandardTableStyles();
  const OUTCOMES = buildOutcomes();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);

  const [filterAnchor, setFilterAnchor] = useState(null);
  const [dateAnchor, setDateAnchor] = useState(null);
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [selectedCallId, setSelectedCallId] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize };
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });

      const { data } = await api.get("/calls/history", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const openFilterPopover = (e) => {
    setDraftFilters(filters);
    setFilterAnchor(e.currentTarget);
  };

  const applyFilters = () => {
    setFilters((f) => ({
      ...f,
      ...Object.fromEntries(POPOVER_FILTER_KEYS.map((key) => [key, draftFilters[key]])),
    }));
    setPage(1);
    setFilterAnchor(null);
  };

  const clearFilters = () => {
    const cleared = Object.fromEntries(POPOVER_FILTER_KEYS.map((key) => [key, ""]));
    setFilters((f) => ({ ...f, ...cleared }));
    setDraftFilters((f) => ({ ...f, ...cleared }));
    setPage(1);
    setFilterAnchor(null);
  };

  const applyDateRange = () => {
    setFilters((f) => ({ ...f, dateFrom: draftFilters.dateFrom, dateTo: draftFilters.dateTo }));
    setPage(1);
    setDateAnchor(null);
  };

  const clearDateRange = () => {
    setFilters((f) => ({ ...f, dateFrom: "", dateTo: "" }));
    setDraftFilters((f) => ({ ...f, dateFrom: "", dateTo: "" }));
    setPage(1);
    setDateAnchor(null);
  };

  const exportCsv = async () => {
    setActionsAnchor(null);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });

      const { data } = await api.get("/calls/history/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "llamadas.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    }
  };

  const dateRangeLabel =
    filters.dateFrom || filters.dateTo
      ? `${filters.dateFrom || "…"} – ${filters.dateTo || "…"}`
      : i18n.t("callHistory.allDates");

  const activeFilterCount = POPOVER_FILTER_KEYS.filter((k) => filters[k]).length;

  return (
    <MainContainer maxWidth={false}>
      <MainHeader>
        <Title>{i18n.t("callHistory.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            startIcon={<CalendarTodayOutlined fontSize="small" />}
            onClick={(e) => {
              setDraftFilters(filters);
              setDateAnchor(e.currentTarget);
            }}
          >
            {dateRangeLabel}
          </Button>

          <Button
            variant={activeFilterCount > 0 ? "contained" : "outlined"}
            color={activeFilterCount > 0 ? "primary" : undefined}
            startIcon={<FilterListOutlined fontSize="small" />}
            onClick={openFilterPopover}
          >
            {i18n.t("callHistory.filters")}{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </Button>

          <Button
            variant="outlined"
            endIcon={<ExpandMore fontSize="small" />}
            onClick={(e) => setActionsAnchor(e.currentTarget)}
          >
            {i18n.t("callHistory.actions")}
          </Button>
          <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={() => setActionsAnchor(null)}>
            <MenuItem onClick={exportCsv}>
              <FileDownloadOutlined fontSize="small" style={{ marginRight: 8 }} />
              {i18n.t("callHistory.exportCsv")}
            </MenuItem>
          </Menu>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Popover
        open={Boolean(dateAnchor)}
        anchorEl={dateAnchor}
        onClose={() => setDateAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <div className={classes.popoverBody}>
          <TextField
            label={i18n.t("callHistory.dateFrom")}
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={draftFilters.dateFrom}
            onChange={(e) => setDraftFilters((f) => ({ ...f, dateFrom: e.target.value }))}
          />
          <TextField
            label={i18n.t("callHistory.dateTo")}
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={draftFilters.dateTo}
            onChange={(e) => setDraftFilters((f) => ({ ...f, dateTo: e.target.value }))}
          />
          <div className={classes.popoverActions}>
            <Button size="small" onClick={clearDateRange}>{i18n.t("callHistory.clear")}</Button>
            <Button size="small" variant="contained" onClick={applyDateRange}>{i18n.t("callHistory.apply")}</Button>
          </div>
        </div>
      </Popover>

      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <div className={classes.popoverBody}>
          <TextField
            select
            label={i18n.t("callHistory.outcome")}
            size="small"
            value={draftFilters.outcome}
            onChange={(e) => setDraftFilters((f) => ({ ...f, outcome: e.target.value }))}
          >
            {OUTCOMES.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label={i18n.t("callHistory.fromLabel")}
            size="small"
            placeholder="+593..."
            value={draftFilters.from}
            onChange={(e) => setDraftFilters((f) => ({ ...f, from: e.target.value }))}
          />
          <TextField
            label={i18n.t("callHistory.toLabel")}
            size="small"
            placeholder="+593..."
            value={draftFilters.to}
            onChange={(e) => setDraftFilters((f) => ({ ...f, to: e.target.value }))}
          />
          <TextField
            select
            label={i18n.t("callHistory.columns.resolved")}
            size="small"
            value={draftFilters.resolved}
            onChange={(e) => setDraftFilters((f) => ({ ...f, resolved: e.target.value }))}
          >
            {buildResolvedOptions().map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={i18n.t("callHistory.columns.sentiment")}
            size="small"
            value={draftFilters.sentiment}
            onChange={(e) => setDraftFilters((f) => ({ ...f, sentiment: e.target.value }))}
          >
            {buildSentimentOptions().map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={i18n.t("callHistory.auditFilter")}
            size="small"
            value={draftFilters.audit}
            onChange={(e) => setDraftFilters((f) => ({ ...f, audit: e.target.value }))}
          >
            {buildAuditOptions().map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          <div className={classes.popoverActions}>
            <Button size="small" onClick={clearFilters}>{i18n.t("callHistory.clear")}</Button>
            <Button size="small" variant="contained" onClick={applyFilters}>{i18n.t("callHistory.apply")}</Button>
          </div>
        </div>
      </Popover>

      <div className={classes.tableWrap}>
      <StandardTableContainer
        height="100%"
        footer={
          <div className={classes.footer}>
            <span>
              {i18n.t("callHistory.pageInfo", {
                page,
                totalPages,
                total: total.toLocaleString(i18n.language?.startsWith("en") ? "en-US" : "es-EC"),
              })}
            </span>

            <div className={classes.pager}>
              <TextField
                select
                size="small"
                variant="standard"
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              >
                {PAGE_SIZES.map((n) => (
                  <MenuItem key={n} value={n}>{i18n.t("callHistory.perPage", { count: n })}</MenuItem>
                ))}
              </TextField>
              <IconButton size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <ArrowBackIosNew style={{ fontSize: 14 }} />
              </IconButton>
              <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                <ArrowForwardIos style={{ fontSize: 14 }} />
              </IconButton>
            </div>
          </div>
        }
      >
        <Table size="small" stickyHeader>
          <TableHead className={tableClasses.head}>
            <TableRow>
              <TableCell>{i18n.t("callHistory.columns.date")}</TableCell>
              <TableCell>{i18n.t("callHistory.columns.duration")}</TableCell>
              <TableCell>{i18n.t("callHistory.columns.cost")}</TableCell>
              <TableCell>{i18n.t("callHistory.columns.direction")}</TableCell>
              <TableCell>{i18n.t("callHistory.columns.from")}</TableCell>
              <TableCell>{i18n.t("callHistory.columns.to")}</TableCell>
              <TableCell>{i18n.t("callHistory.columns.connection")}</TableCell>
              <TableCell align="center">{i18n.t("callHistory.columns.resolved")}</TableCell>
              <TableCell align="center">{i18n.t("callHistory.columns.sentiment")}</TableCell>
              <TableCell>{i18n.t("callHistory.columns.outcome")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const info = outcomeInfo(r.outcome);
              const sentiment = sentimentInfo(r.auditSentiment || r.analysisSentiment);
              const resolved = r.auditSuccessful ?? r.analysisSuccessful;
              const auditTitle = r.auditedAt
                ? i18n.t("callHistory.auditedBy", { name: r.auditedByName || "—" })
                : "";
              return (
                <TableRow key={r.id} hover style={{ cursor: "pointer" }} onClick={() => setSelectedCallId(r.id)}>
                  <TableCell>{formatFecha(r.createdAt)}</TableCell>
                  <TableCell className={classes.mono}>{formatDuration(r.durationSeconds)}</TableCell>
                  <TableCell className={classes.mono}>${r.estimatedCostUsd.toFixed(2)}</TableCell>
                  <TableCell>
                    <span className={classes.direction}>
                      <CallReceivedOutlined style={{ fontSize: 16 }} />
                      {i18n.t("callHistory.inbound")}
                    </span>
                  </TableCell>
                  <TableCell className={classes.mono}>
                    {r.callerNumber || "—"}
                  </TableCell>
                  <TableCell className={classes.mono}>{r.toNumber || "—"}</TableCell>
                  <TableCell>
                    <span className={classes.connection}>
                      <PhoneInTalkOutlined className={classes.connectionIcon} />
                      {r.connectionName || "—"}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    {resolved === null ? (
                      "—"
                    ) : (
                      <Tooltip title={r.auditSuccessful !== null ? auditTitle : ""}>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={resolved ? "success" : "error"}
                          icon={r.auditSuccessful !== null ? <VerifiedOutlined /> : undefined}
                          label={resolved ? i18n.t("callHistory.yes") : i18n.t("callHistory.no")}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {sentiment ? (
                      <Tooltip title={r.auditSentiment ? auditTitle : ""}>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={sentiment.chipColor}
                          icon={r.auditSentiment ? <VerifiedOutlined /> : undefined}
                          label={sentiment.label}
                        />
                      </Tooltip>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {r.outcome === "transferred" ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={transferResultInfo(r.transferDialStatus).chipColor}
                        label={transferResultInfo(r.transferDialStatus).label}
                      />
                    ) : (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={OUTCOME_CHIP_COLOR[r.outcome] || "default"}
                        label={info.label}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {!loading && rows.length === 0 && (
          <div className={tableClasses.empty}>
            {total === 0 && activeFilterCount === 0 && !filters.dateFrom
              ? i18n.t("callHistory.emptyNoFilters")
              : i18n.t("callHistory.emptyFiltered")}
          </div>
        )}
        {loading && (
          <div className={classes.loadingRow}>
            <CircularProgress size={22} />
          </div>
        )}
      </StandardTableContainer>
      </div>

      <CallDetailModal
        open={Boolean(selectedCallId)}
        callId={selectedCallId}
        onClose={() => setSelectedCallId(null)}
        onAudited={(updated) =>
          setRows((prev) =>
            prev.map((row) =>
              row.id === updated.id
                ? {
                    ...row,
                    auditSuccessful: updated.auditSuccessful,
                    auditSentiment: updated.auditSentiment,
                    auditComment: updated.auditComment,
                    auditedAt: updated.auditedAt,
                    auditedByName: updated.auditedByName,
                  }
                : row
            )
          )
        }
      />
    </MainContainer>
  );
};

export default CallHistory;
